import { defineStore } from 'pinia';
import { DealerService, ProposalService } from '../services';
import { useAuthStore } from './auth-store';

import { ApplicationType_Enum, LoanStatusType_Enum } from 'src/types/enums';
import { LoanApplication } from 'src/types/loan-application';
import type { ApplicationNote, ConvertFullProposalRequest, CreditCheck, Enquiry, Vehicle } from 'src/types/loan-application';
import type { QuickQuote, BespokeRate, Quote } from 'src/types/quote';
import type { SocketMessage } from 'src/types/socket';
import type { GetApplicationsRequest, GetEnquiriesRequest } from 'src/types/dealer';
import { useDifferenceInDays, EVENTS, logEvent, useMapQuickQuoteToLoanApplication, trimDocumentReqs } from 'src/functions';
import proposalService from 'src/services/proposal-service';
import { PortalNote } from 'src/types/notification/portal-note';
import PortalNoteType_Enum from 'src/types/enums/note-type';
import { Proposal } from '@cf247/pricing';

let globalWebSocket: WebSocket | null = null;

const notificationSound = new Audio(require('assets/sounds/notification.mp3'));

export const useProposalStore = defineStore('proposal', {
  state: () => ({
    applications: [] as LoanApplication[],
    quickQuotes: [] as LoanApplication[],
    enquiries: [] as Enquiry[],
    selectedApplicationId: null as (null | number | string),
    selectedApplicationObject: null as (null | LoanApplication),
    notes: [] as { brokerReference?: number, notes: PortalNote[], documents: ApplicationNote[] }[],
    proposalToCreate: null as (LoanApplication | null),
    proposalToConvert: null as (LoanApplication | null),
    stockProposal: null as (LoanApplication | null),
    defaultPcpMileage: 8000,
    bespokeRates: null as BespokeRate[] | null,
    connectedToSocket: false,
    lastMessage: null as (null | SocketMessage),
    savedQuotes: null as (null | Quote[]),
    initialisingQuotes: false
  }),
  actions: {
    getData(days: number) {
      console.log("Getting data")
      return new Promise(async (resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }
        const tasks = [];
        tasks.push(this.getApplications(days).catch((error) => reject(error)))
        tasks.push(this.getQuickQuotes(days).catch((error) => reject(error)))
        tasks.push(this.getEnquiries(days).catch((error) => reject(error)))
        await Promise.all(tasks);
        resolve(true);
      })
    },
    getApplications(days: number) {
      return new Promise((resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }

        const aids = useAuthStore().affiliateUserIds && useAuthStore().affiliateUserIds.length ? 
          useAuthStore().affiliateUserIds.map(a => a.toString()) :
          [useAuthStore().affiliateUserId!.toString()];

        const isASM = useAuthStore().isIntranetUser && !useAuthStore().user.skills.find((s: number) => s === 74);

        const isIntranetUser = useAuthStore().isIntranetUser && useAuthStore().user.skills.find((s: number) => s === 74);

        let request = {
          affiliateUserIds: aids,
          days: aids.length > 1000 ? 2 : aids.length > 20 ? 15 : days,
          includeProposals: true,
          expandPrime: false,
          excludedStates: [4, 5, 6, 7, 8],
          useCreatedDate: isASM || isIntranetUser ? true : false
        } as GetApplicationsRequest;

        ProposalService.getApplications(request).then(res => {
          const route = this.router.currentRoute.value;
          // Filter out apps with no app id after 2 days
          const timestamp = new Date().getTime() - (2 * 24 * 60 * 60 * 1000)
          res = res.filter(r => r.state === LoanStatusType_Enum.Underwriting && new Date(r.created).getTime() < timestamp && r.brokerReference === 0 ? false : true)

          // Sort by last updated
          res = res.sort((a, b) => new Date(a.lastUpdated ?? a.created).getTime() < new Date(b.lastUpdated ?? b.created).getTime() ? 1 : -1);

          // Manually set status to NTU if in underwriting after 7 days
          res.forEach(r => {
            if (useDifferenceInDays(new Date(r.created), new Date()) >= 7 && r.state === LoanStatusType_Enum.Underwriting) {
              r.state = LoanStatusType_Enum['NTU Other'];
            }
              if(r.isNtuFlagged){
                r.state = LoanStatusType_Enum['NTU Manual'];
              }
          })

          let onApp: LoanApplication | null = null;

          if(route.name === "Customer"){
            const app = this.applications.find(a => a.brokerReference === Number(route.params.id));
            if(app){
              onApp = app;
            }
          }

          if(onApp){
           res[res.findIndex(a => a.brokerReference === onApp!.brokerReference)] = onApp;
          }

          this.applications = res;

          resolve(res);
        }).catch((error) => {
          console.log(error)
          reject(error);
        })
      })
    },
    getApplication(brokerReference: number, aid: string) {
      return new Promise((resolve, reject) => {
        proposalService.getApplication(brokerReference, Number(aid)).then(res => {
          if (res) {
            const i = this.applications.findIndex(a => a.cosmosId === res.cosmosId);
            if(i > -1){
              this.applications.splice(i, 1);
            }
            this.applications.push(new LoanApplication(res));
          }
          resolve(res);
        }).catch((error) => {
          console.log(error)
          reject(error);
        })
      })
    },
    setApplications(data: LoanApplication[]) {
      this.applications = data;
    },
    selectApplication(id: number | null | string) {
      this.selectedApplicationId = id;
      if (id) {
        logEvent(EVENTS.PROPOSAL_VIEW);
      }
    },
    updateState(brokerReference: number, state: LoanStatusType_Enum) {
      return new Promise((resolve, reject) => {
        const app = this.applications.find(a => a.brokerReference == brokerReference);
        if (app) {
          app.state = state as LoanStatusType_Enum;
        }
        resolve(true);
      })
    },
    toggleNtu(brokerReference:number, isNtu:boolean){
      return new Promise((resolve, reject) => {
        ProposalService.toggleNTU(brokerReference, isNtu).then(res => {
          const app = this.applications.find(a => a.brokerReference == brokerReference);
          if(app){
            app.isNtuFlagged = isNtu;
            if (isNtu)
            {
              app.state = LoanStatusType_Enum['NTU Manual'];
            }
            else {
              proposalService.getApplication(brokerReference, Number(app.affiliateUserId)).then(res => {
                app.state = res.state;
              })
            }
          }
          resolve(res);
        }).catch(error => {
          reject(error);
        }) 
      })
    },
    deleteQuickQuote(quoteId: string) {
      return new Promise((resolve, reject) => {
        ProposalService.deleteQuickQuote(quoteId).then(res => {
          if (this.router) {
            this.router.push({ name: 'Customers' });
          }
          resolve(res);
        }).catch(error => {
          reject(error);
        });
      });
    },
    getQuickQuotes(days: number) {
      return new Promise((resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }

        const aids = useAuthStore().affiliateUserIds && useAuthStore().affiliateUserIds.length ? 
          useAuthStore().affiliateUserIds.map(a => a.toString()) :
          [useAuthStore().affiliateUserId!.toString()];

        days = aids.length > 1000 ? 2 : aids.length > 20 ? 7 : days

        ProposalService.getQuickQuotes(aids, days).then(res => {
          const route = this.router.currentRoute.value;

          const mapped = res.map(r => useMapQuickQuoteToLoanApplication(r));
 
          let onApp: LoanApplication | null = null;

          if(route.name === "Customer"){
            const app = this.applications.find(a => a.cosmosId === route.params.id);
            if(app){
              onApp = app;
            }
          }

          if(onApp){
            mapped[mapped.findIndex(a => a.cosmosId === onApp!.quickQuoteId)] = onApp;
          }

          this.quickQuotes = mapped.reverse();
          
          resolve(res);
        }).catch((error) => {
          console.log(error)
          reject(error);
        })
      })
    },
    clearQuickQuotes() {
      this.quickQuotes = [];
    },
    getEnquiries(days: number) {
      return new Promise((resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }

        const aids = useAuthStore().affiliateUserIds && useAuthStore().affiliateUserIds.length ? 
          useAuthStore().affiliateUserIds.map(a => a.toString()) :
          [useAuthStore().affiliateUserId!.toString()];

        days = aids.length > 1000 ? 2 : aids.length > 20 ? 7 : days

        const request = {
          affiliateUserIds: aids,
          days
        } as GetEnquiriesRequest;
        ProposalService.getEnquiries(request).then(res => {
          this.enquiries = res;          
          resolve(res);
        }).catch((error) => {
          console.log(error)
          reject(error);
        })
      })
    },
    createEnquiry(enquiry: Enquiry): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ProposalService.createEnquiry(enquiry).then(res => {
          if (res) {
            enquiry.id = res;
            this.enquiries.unshift(enquiry);
            logEvent(EVENTS.ENQUIRY_CREATE_NEW);
            resolve(true);
          }
          reject(false);
          
          
        }).catch(error => {
          reject(error);
        })
      })
    },
    updateAffordability(request: ConvertFullProposalRequest){

      return new Promise<boolean>((resolve, reject) => {
        ProposalService.convertToFullProposal(request).then(res => {
          if (res) {
            const app = this.applications.find(a => a.brokerReference === request.applicationId);
            if (app) {
              app.applicationType = ApplicationType_Enum['Full Proposal'];
              app.affordability = request.affordability;
              app.customer!.bankAccountName = request.bankAccountName;
              app.customer!.bankAccountNumber = request.bankAccountNumber;
              app.customer!.bankSortCode = request.bankSortCode;
              app.customer!.bankName = request.bankName;
              app.customer!.bankMonthsAt = request.bankMonthsAt;
              app.customer!.grossAnnualIncome = request.grossAnnualIncome;
              app.customer!.numberOfDependants = request.numberOfDependents;
              app.customer!.nationalityOnLicence = request.nationalityOnLicence;
              app.customer!.employmentHistory = request.employmentHistory;
              app.isVehicleBusinessUse = request.businessUse;
            }
            resolve(true);
          }
          resolve(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    getAvailableProposalsForApplication(brokerReference: number): Promise<Proposal[]> {
      return new Promise<Proposal[]>((resolve, reject) => {

        const app = this.applications.find(a => a.brokerReference === brokerReference);

        if(!app){
          resolve([]);
          return;
        }
        
        const isPrime = (app.proposals === null || app.proposals === undefined || app.proposals.length === 0) || (app.proposals && app.proposals.find(p => p.isPrime || p.lenderId === 49)) ? true : false;

        if(!app.brokerReference || app.brokerReference === 0){
          resolve([]);
        }

        ProposalService.getAvailableProposals(app.brokerReference, isPrime, Number(app.affiliateUserId)).then(async res => {

          if(res && res.length > 0){
            app.proposals = res;
          }

          resolve(res);

        }).catch(error => {
          resolve([])
        })
      })
    },
    getCreditCheckForApplication(brokerReference: number, aid: number): Promise<CreditCheck> {
      return new Promise((resolve, reject) => {
        if (this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck) {
          resolve(this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck!);
          return
        }
        this.applications.find(a => a.brokerReference === brokerReference)!.loadingCreditCheck = true;
        ProposalService.getCreditCheck(brokerReference, aid).then(res => {
          this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck = res;
          resolve(res);
        }).catch(error => {
          reject(error);
        }).finally(() => {
          this.applications.find(a => a.brokerReference === brokerReference)!.loadingCreditCheck = false
        })
      })
    },
    getPayoutPackageForApplication(brokerReference: number): Promise<boolean> {
      return new Promise((resolve, reject) => {
        const app = this.applications.find(a => a.brokerReference === brokerReference);
        if(!app){
          resolve(false);
          return;
        }
        ProposalService.getPayoutPackage(app.brokerReference, app.affiliateUserId).then(res => {
          if (res) {
            if(app.customer?.drivingLicenceType){
              res = trimDocumentReqs(res, app.customer?.drivingLicenceType!);
            }
            app.payoutPack = res;
            resolve(true);
          }
          resolve(false);
        }
        ).catch(error => {
          resolve(false);
        })
      })
    },
    getNotes(brokerReference: number) {
      this.notes = [];
      return new Promise<boolean>((resolve, reject) => {
        const index = this.notes.findIndex(n => (brokerReference && n.brokerReference === brokerReference));
        if (index > -1) {
          this.notes.splice(index, 1);
        }
        let docs = [] as ApplicationNote[];
        let notes = [] as PortalNote[];

        const tasks = [] as Promise<any>[];
        
        tasks.push(ProposalService.getUploadedDocuments(brokerReference).then(res => {
          if (res && res.length > 0) {
            docs = res;
          }
        }).catch(error => {
          console.log("no docs")
        }))
        tasks.push(ProposalService.getPortalNotes(brokerReference).then(res => {
          if (res && res.length > 0) {
            notes = res;
          }
        }).catch(error => {
          console.log("no notes")
        }))
        Promise.all(tasks).then(() => {
          this.notes.push({
            brokerReference,
            notes,
            documents: docs
          });
          resolve(true);
        }).catch(error => {
          console.log(error)
          reject(false);
        })
      })
    },
    refreshNotes(brokerReference: number) {
      return new Promise((resolve, reject) => {
        const ns = this.notes.find(n => (brokerReference && n.brokerReference == brokerReference));
        console.log("NS", ns)
        if(ns){
          proposalService.getPortalNotes(brokerReference).then(res => {
            if (res && res.length > 0) {
              ns.notes = res;
            }
            resolve(true);
          }).catch(error => {
            console.log(error)
            reject(false);
          })
        }
        else{
          proposalService.getPortalNotes(brokerReference).then(res => {
            if (res && res.length > 0) {
              this.notes.push({ 
                brokerReference,
                notes: res,
                documents: []
              });
            }
            resolve(true);
          }).catch(error => {
            console.log(error)
            reject(false);
          })
        }
      })
    },
    refreshNoteDocuments(brokerReference: number) {
      return new Promise((resolve, reject) => {
        const ns = this.notes.find(n => (brokerReference && n.brokerReference == brokerReference));
        if(ns){
          proposalService.getUploadedDocuments(brokerReference).then(res => {
            if (res && res.length > 0) {
              ns.documents = res;
            }
            resolve(true);
          }).catch(error => {
            console.log(error)
            reject(false);
          })
        }
        else{
          proposalService.getUploadedDocuments(brokerReference).then(res => {
            if (res && res.length > 0) {
              this.notes.push({ 
                brokerReference,
                notes: [],
                documents: res
              });
            }
            resolve(true);
          }).catch(error => {
            console.log(error)
            reject(false);
          })
        }
      })
    },
    addNote(note: ApplicationNote): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ProposalService.addNote(note).then(res => {
          if (res) {
            const e = this.notes.find(n => (note.brokerReference && n.brokerReference === note.brokerReference));
            if (e) {
              e!.notes.push({
                text: note.text,
                userName: note.createdBy,
                department: note.dealerName,
                created: note.createdOn,
                createdBy: "DealerPortal.Notifications",
                type: PortalNoteType_Enum.Communication,
                brokerReference: note.brokerReference,
                subtext: null,
                id: ""
              } as PortalNote);
            }
            else {
              this.notes.push({
                brokerReference: note.brokerReference!,
                notes: [
                  {
                    text: note.text,
                    userName: note.createdBy,
                    department: note.dealerName,
                    created: note.createdOn,
                    createdBy: "DealerPortal.Notifications",
                    type: PortalNoteType_Enum.Communication,
                    brokerReference: note.brokerReference,
                    id: "1",
                  } as PortalNote
                ],
                documents: []
              })
            }

            resolve(true);
          }
          resolve(false)
        }).catch(error => {
          console.log(error)  
          reject(error);
        })
      })
    },
    createProposal(proposal: LoanApplication): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ProposalService.createApplication(proposal).then(res => {
          if (res) {

            const qqid = this.quickQuotes.findIndex(q => q.quickQuoteId === proposal.quickQuoteId);
            if (qqid > -1) {
              this.quickQuotes.splice(qqid, 1);
            }

            proposal.decisionStatus = 2;
            proposal.cosmosId = res;
            proposal.state = 0;
            proposal.isQuickQuote = false;

            this.applications.unshift(proposal);

            setTimeout(() => { this.getApplications(30) }, 60000)
            resolve(true);
          }
          reject(false);
          if (proposal.applicationType == ApplicationType_Enum['Eligibility'])
          {
            logEvent(EVENTS.ELIGIBILITY_CREATE_NEW, { term: proposal.term })
          }
          if (proposal.applicationType == ApplicationType_Enum['Full Proposal'])
          {
            logEvent(EVENTS.PROPOSAL_CREATE_NEW, { term: proposal.term })
          }
          
        }).catch(error => {
          reject(error);
        })
      })
    },
    createQuickQuote(quickQuote: QuickQuote): Promise<string> {
      return new Promise((resolve, reject) => {
        ProposalService.createQuickQuote(quickQuote).then(res => {
          if (res) {
            this.getQuickQuotes(30).then(response => {
              resolve(res);
              return;
            }).catch(() => {
              reject(false)
            })
            logEvent(EVENTS.QUICK_QUOTE_CREATED);
          }
          else {
            reject(false)
          }
        }).catch(error => {
          reject(error);
        })
      })
    },
    changeVehicle(applicationId: number, vehicle: Vehicle): Promise<boolean>{
      return new Promise((resolve, reject) => {
        ProposalService.changeVehicle(applicationId, vehicle).then(res => {
          if (res) {
            const app = this.applications.find(a => a.brokerReference === applicationId);
            if (app) {
              app.vehicle = vehicle;
              resolve(true);
            }
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })  
    },
    getQuotes(applicationId: number): Promise<Quote[]> {
      return new Promise<Quote[]>((resolve, reject) => {
        this.savedQuotes = null;
        ProposalService.getQuotes(applicationId).then(res => {

          if(res && res.length > 0) {
            this.savedQuotes = res.filter(q => !q.isArchived);
          }
          else{
            this.savedQuotes = null;
          }

          // need to archive any quotes that are from banned lenders
          const app = this.applications.find(a => a.brokerReference === applicationId);
          const as = useAuthStore();
          const archiveTasks: Promise<any>[] = [];

          Promise.all(archiveTasks).then(() => {
            resolve(res);
          }).catch(() => {
            resolve(res);
          });
        }).catch(error => {
          this.savedQuotes = null;
          reject(error);
        })
      })
    },
    createQuote(quote: Quote): Promise<Quote> {
      return new Promise((resolve, reject) => {
        ProposalService.saveQuote(quote).then(res => {
          if (res) {
            if (this.savedQuotes) {
              res.quoteId = this.savedQuotes[this.savedQuotes.length - 1].quoteId ? this.savedQuotes[this.savedQuotes.length - 1].quoteId! + 1 : 1;
              this.savedQuotes.push(res);
            }
            else {
              res.quoteId = 1;
              this.savedQuotes = [res];
            }
            resolve(res);
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    proposeQuote(applicationId: number, quoteId: number): Promise<Quote> {
      return new Promise((resolve, reject) => {
        ProposalService.proposeQuote(applicationId, quoteId).then(res => {
          if (res) {
            const i = this.savedQuotes!.findIndex(q => q.quoteId === quoteId);
            if (i > -1) {
              this.savedQuotes![i].isProposed = true;
              resolve(this.savedQuotes![i]);
              return;
            }
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    acceptQuote(applicationId: number, quoteId: number): Promise<Quote> {
      return new Promise((resolve, reject) => {
        ProposalService.acceptQuote(applicationId, quoteId).then(res => {
          if (res) {
            const i = this.savedQuotes!.findIndex(q => q.quoteId === quoteId);
            if (i > -1) {
              this.savedQuotes![i].isAccepted = true;
              resolve(this.savedQuotes![i]);
            }
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    archiveQuote(applicationId: number, quoteId: number): Promise<Quote> {
      return new Promise((resolve, reject) => {
        ProposalService.archiveQuote(applicationId, quoteId).then(res => {
          if (res) {
            const i = this.savedQuotes!.findIndex(q => q.quoteId === quoteId);
            if (i > -1) {
              this.savedQuotes!.splice(i, 1);
              resolve(this.savedQuotes![i]);
            }
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    getBespokeRates(aid: number): Promise<boolean> {
      return new Promise((resolve, reject) => {
        DealerService.getBespokeRates(aid).then(res => {
          if (res && res.length > 0) {
            this.bespokeRates = res;
            resolve(true);
            return
          }
          this.bespokeRates = null;
          resolve(false)
        }).catch(error => {
          resolve(false)
          resolve(false)
        })
      })
    },
    setBespokeRates(rates: BespokeRate[] | null) {
      this.bespokeRates = rates;
    },
    reapply(proposal: LoanApplication) {
      this.proposalToCreate = proposal;
    },
    clearReapply() {
      this.proposalToCreate = null;
    },
    convert(proposal: LoanApplication) {
      this.proposalToConvert = proposal;
    },
    clearConvert() {
      this.proposalToConvert = null;
    },
    setStockProposal(proposal: LoanApplication) {
      this.stockProposal = proposal;
    },
    clearStockProposal() {
      this.stockProposal = null;
    },
    // clearSubscription() {
    //   if (globalWebSocket) {
    //     globalWebSocket.close();
    //     setTimeout(() => {
    //       if(globalWebSocket){
    //         globalWebSocket.onopen = null;
    //         globalWebSocket.onclose = null;
    //         globalWebSocket.onerror = null;
    //         globalWebSocket.onmessage = null;
    //         globalWebSocket = null;
    //         this.connectedToSocket = false;
    //       }
    //     }, 1000);
    //   }
    // },
    // subscribeGlobal() {

    //   if (!useAuthStore().affiliateUserId && (!useAuthStore().affiliateUserIds || useAuthStore().affiliateUserIds.length === 0)) {
    //     return;
    //   }

    //   this.clearSubscription();
      
    //   return new Promise((resolve, reject) => {
    //     // Get socket url from sync service
    //     ProposalService.subscribeGlobal().then(url => {

    //       // Connect to new socket
    //       globalWebSocket = new WebSocket(url);
    //       globalWebSocket.onopen = () => { this.connectedToSocket = true; }
    //       globalWebSocket.onclose = () => { this.connectedToSocket = false; }
    //       globalWebSocket.onerror = (error) => console.log("socket - error", error)
    //       globalWebSocket.onmessage = event => { this.handleMessage(JSON.parse(event.data) as SocketMessage); }

    //       resolve(true);
    //     }).catch(error => {
    //       setTimeout(() => {
    //         this.subscribeGlobal();
    //       }, 30000)
    //       resolve(false);
    //     })
    //   })
    // },
    // handleMessage(message: SocketMessage) {
    //   if (
    //     !message ||
    //     !message.data ||
    //     !message.affiliateUserId ||
    //     (useAuthStore().affiliateUserIds.length ? !useAuthStore().affiliateUserIds.find(a => a == message.affiliateUserId) : useAuthStore().affiliateUserId != message.affiliateUserId)
    //   ) {
    //     return;
    //   }
    //   if (message.messageType === SocketMessage_Enum.ApplicationStateChanged &&
    //     this.lastMessage &&
    //     this.lastMessage.messageType === SocketMessage_Enum.ApplicationStateChanged) {
    //     const d = message.data as SocketMessage_AppStateChanged;
    //     const c = this.lastMessage.data as SocketMessage_AppStateChanged;
    //     if(this.lastMessage && 
    //       c.LoanApplicationId === d.LoanApplicationId &&
    //       c.Status === d.Status &&
    //       c.StatusReasonId === d.StatusReasonId){
    //       return;
    //     }
    //   }
    //   this.lastMessage = message;
    //   switch (message.messageType) {
    //     case SocketMessage_Enum.ApplicationStateChanged:
    //       this.appStateChanged(message.data as SocketMessage_AppStateChanged);
    //       break;
    //     default:
    //       break;
    //   }
    // },
    // appStateChanged(data: SocketMessage_AppStateChanged, notify: boolean = true) {
      
    //   const newState = useMapStateChange(data.Status, data.StatusReasonId);
    //   const app = this.applications.find(a => a.brokerReference == data.LoanApplicationId);
      
    //   if (app && app.brokerReference) {

    //     // refetch dealsheet if app has one
    //     if(app.payoutPack && app.payoutPack.dealSheet && app.payoutPack.dealSheet.intranetLoanDealPreId){
    //       this.getPayoutPackageForApplication(app.brokerReference).catch(error => { console.log("Unable to get payout package", error) });
    //     }

    //     if (app.state !== newState && notify) {
    //       app.lastSocketUpdate = new Date().getTime();
    //       setTimeout(() => {
    //         let app = this.applications.find(a => a.brokerReference === data.LoanApplicationId);
    //         app ? app.lastSocketUpdate = null : null;
    //       }, 60000)
    //       app.state = newState;
    //       if (notify && (newState === LoanStatusType_Enum['Ready To Deal'] || newState === LoanStatusType_Enum.Completed)) {
    //         notificationSound.play().catch(error => { console.log("Unable to play notification sound, user not interacted with page")});
    //         Notify.create({
    //           position: "top",
    //           html: true,
    //           message: `${app?.customer ? (app?.customer.firstName + " " + app?.customer.lastName + "<div class=\"tw-mt-1\"></div>") : ''}Application has moved into ${LoanStatusType_Enum[newState]}`,
    //           avatar: app.vehicle?.image ?? undefined,
    //           actions: [
    //             { label: 'View', color: 'white', noCaps: true, handler: () => this.router.push(`/customer/${app.brokerReference}`) },
    //             { icon: 'mdi-close', color: 'white', round: true,  size: "sm", handler: () => { } }
    //           ],
    //           classes: 'tw-bg-slate-800',
    //           multiLine: false,
    //           timeout: 10000,
    //           progress: true
    //         })
    //       }
    //     }  

    //     this.getAvailableProposalsForApplication(app.brokerReference).catch(error => { console.log("Unable to get proposals", error) });
    //   }
    //   else {
    //     const aid = useAuthStore().affiliateUserId;
    //     ProposalService.getApplication(data.LoanApplicationId, aid).then(res => {

    //       res.state = newState;
  
    //       const old = this.applications.findIndex(a => a.cosmosId === res.cosmosId);

    //       let redirect = false;
    //       if (old > -1) {
    //         this.applications.splice(old, 1);
    //         redirect = true;
    //       }

    //       this.applications.unshift(res);

    //       if (redirect && this.router.currentRoute.value.fullPath.includes(`${res.cosmosId}`)) {
    //         this.router.push({ path: `/customer/${res.brokerReference}` });
    //       }

    //       res.lastSocketUpdate = new Date().getTime();

    //       setTimeout(() => {
    //         let app = this.applications.find(a => a.brokerReference === res.brokerReference);
    //         app ? app.lastSocketUpdate = null : null;
    //       }, 60000)

    //       if (newState === LoanStatusType_Enum['Ready To Deal'] || newState === LoanStatusType_Enum.Completed) {
    //         notificationSound.play().catch(error => { console.log("Unable to play notification sound, user not interacted with page")});

    //         Notify.create({
    //           position: "top",
    //           html: true,
    //           message: `${res.customer ? (res.customer.firstName + " " + res.customer.lastName + '<div class=\"tw-mt-1\"></div>') : ''}Application has moved into ${LoanStatusType_Enum[newState]}`,
    //           avatar: res.vehicle?.image ?? undefined,
    //           actions: [
    //             { label: 'View', color: 'white', handler: () => this.router.push(`/customer/${res.brokerReference}`) },
    //             { icon: 'mdi-close', color: 'white', round: true,  size: "sm", handler: () => { } }
    //           ],
    //           multiLine: false,
    //           timeout: 10000,
    //           progress: true,
    //           classes: 'tw-bg-slate-800'
    //         })
    //       }

    //       this.getAvailableProposalsForApplication(res.brokerReference).catch(error => { console.log("Unable to get proposals", error) });

    //       if (res.createdBy === "Customer (Web)" || res.createdBy === "Customer (Link)") {
    //         this.getQuickQuotes(30);
    //       }
    //     }).catch(error => {
    //       console.log(error);
    //       setTimeout(() => { this.appStateChanged(data) }, 30000)
    //     })
    //   }

    // }
  },
  persist: true
});

